import { toast } from "sonner";

export const SHOPIFY_API_VERSION = "2025-07";
export const SHOPIFY_STORE_PERMANENT_DOMAIN = "at1ed7-zz.myshopify.com";
export const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
// Public Storefront API access token (safe to commit — it's a published key
// scoped to read-only storefront data, including `unauthenticated_read_metaobjects`).
export const SHOPIFY_STOREFRONT_TOKEN =
  (import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN as string | undefined) ??
  "c0bb90971d9a91b41fdbe78788f6b681";

export interface ShopifyImage {
  url: string;
  altText: string | null;
}

export interface ShopifySellingPlan {
  id: string;
  name: string;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: { amount: string; currencyCode: string };
  availableForSale: boolean;
  /** Null when the merchant hides inventory; otherwise remaining units. */
  quantityAvailable: number | null;
  /** True when the variant has 0 on hand but "continue selling when out of stock" is enabled (pre-order / backorder). */
  currentlyNotInStock: boolean;
  /** Raw Storefront allocations — use `getSellingPlans()` to read the normalized list. */
  sellingPlanAllocations?: {
    edges: Array<{ node: { sellingPlan: ShopifySellingPlan } }>;
  };
  selectedOptions: Array<{ name: string; value: string }>;
}

/** Normalize selling plans attached to a variant (Stoq pre-order, subscriptions, etc.). */
export function getSellingPlans(variant: ShopifyVariant): ShopifySellingPlan[] {
  return (
    variant.sellingPlanAllocations?.edges?.map((e) => e.node.sellingPlan) ?? []
  );
}



export interface ShopifyCartLine {
  id: string;
  quantity: number;
  merchandise: ShopifyVariant & {
    image: ShopifyImage | null;
    product: {
      handle: string;
      title: string;
      featuredImage: ShopifyImage | null;
    };
  };
}

export interface ShopifyCartSnapshot {
  id: string;
  checkoutUrl: string;
  totalQuantity: number;
  lines: { edges: Array<{ node: ShopifyCartLine }> };
}

export interface ShopifyMetafield {
  value: string;
}

export interface ShopifyProductNode {
  id: string;
  title: string;
  description: string;
  descriptionHtml: string;
  handle: string;
  productType: string;
  tags: string[];
  vendor: string;
  priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
  featuredImage: ShopifyImage | null;
  images: { edges: Array<{ node: ShopifyImage }> };
  variants: { edges: Array<{ node: ShopifyVariant }> };
  options: Array<{ name: string; values: string[] }>;
  keyNotes?: { references?: { nodes: CharacterCardMetaobject[] } } | null;
  subtitle?: ShopifyMetafield | null;
  storyTagline?: ShopifyMetafield | null;
  storyClose?: ShopifyMetafield | null;
  buyNote?: ShopifyMetafield | null;
  accents?: ShopifyMetafield | null;
  skuDisplay?: ShopifyMetafield | null;
  installment?: ShopifyMetafield | null;
}


export interface ShopifyProduct {
  node: ShopifyProductNode;
}

export interface CharacterCardMetaobject {
  id: string;
  handle: string;
  type: string;
  fields: Array<{
    key: string;
    value: string | null;
    reference?: { image?: ShopifyImage } | null;
  }>;
}

function extractOperationName(query: string): string {
  const m = query.match(/\b(query|mutation)\s+(\w+)/);
  return m ? m[2] : "AnonymousOperation";
}

function summarizeVariables(vars: Record<string, any>): Record<string, any> {
  // Avoid logging huge payloads (e.g. long arrays); keep it shallow & bounded.
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(vars)) {
    if (v == null) out[k] = v;
    else if (Array.isArray(v)) out[k] = `Array(${v.length})`;
    else if (typeof v === "string") out[k] = v.length > 120 ? v.slice(0, 117) + "..." : v;
    else if (typeof v === "object") out[k] = "[object]";
    else out[k] = v;
  }
  return out;
}

export interface StorefrontErrorDetail {
  operation: string;
  status?: number;
  durationMs: number;
  requestId?: string | null;
  variables: Record<string, any>;
  httpError?: string;
  graphqlErrors?: Array<{ message: string; path?: any; extensions?: any }>;
  bodyPreview?: string;
  exception?: { name: string; message: string; stack?: string };
}

function reportStorefrontError(detail: StorefrontErrorDetail): void {
  // Structured console log — easy to grep server-side and in the browser.
  console.error("[shopify-storefront]", JSON.stringify(detail, null, 2));

  // Optional Sentry hook. Activates automatically if window.Sentry is loaded.
  // To enable Sentry: add the Sentry browser SDK script in __root.tsx head and
  // call Sentry.init({ dsn: "..." }); no further changes required here.
  try {
    const sentry = (globalThis as any)?.Sentry;
    if (sentry && typeof sentry.captureMessage === "function") {
      sentry.captureMessage(`Shopify Storefront: ${detail.operation} failed`, {
        level: "error",
        extra: detail,
        tags: {
          source: "shopify-storefront",
          operation: detail.operation,
          status: String(detail.status ?? "n/a"),
        },
      });
    }
  } catch {
    // Never let logging itself break the request flow.
  }
}

export async function storefrontApiRequest<T = any>(
  query: string,
  variables: Record<string, any> = {},
): Promise<{ data: T } | null> {
  const operation = extractOperationName(query);
  const startedAt = Date.now();
  let response: Response | undefined;
  try {
    response = await fetch(SHOPIFY_STOREFRONT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });

    const durationMs = Date.now() - startedAt;
    const requestId =
      response.headers.get("x-request-id") ||
      response.headers.get("x-shopify-request-id");

    if (response.status === 402) {
      toast.error("Shopify: Payment required");
      reportStorefrontError({
        operation,
        status: 402,
        durationMs,
        requestId,
        variables: summarizeVariables(variables),
        httpError: "Payment required (402) — Shopify billing inactive",
      });
      return null;
    }

    if (!response.ok) {
      let bodyPreview: string | undefined;
      try {
        const text = await response.text();
        bodyPreview = text.length > 500 ? text.slice(0, 500) + "..." : text;
      } catch {
        // ignore
      }
      reportStorefrontError({
        operation,
        status: response.status,
        durationMs,
        requestId,
        variables: summarizeVariables(variables),
        httpError: `HTTP ${response.status} ${response.statusText}`,
        bodyPreview,
      });
      return null;
    }

    const json = await response.json();
    if (json.errors && Array.isArray(json.errors) && json.errors.length > 0) {
      reportStorefrontError({
        operation,
        status: response.status,
        durationMs,
        requestId,
        variables: summarizeVariables(variables),
        graphqlErrors: json.errors.map((e: any) => ({
          message: e?.message,
          path: e?.path,
          extensions: e?.extensions,
        })),
      });
      // Surface a partial response when present; otherwise treat as failure.
      if (!json.data) return null;
    }
    return json;
  } catch (err) {
    const durationMs = Date.now() - startedAt;
    const e = err as Error;
    reportStorefrontError({
      operation,
      status: response?.status,
      durationMs,
      variables: summarizeVariables(variables),
      exception: { name: e?.name ?? "Error", message: e?.message ?? String(err), stack: e?.stack },
    });
    return null;
  }
}


const PRODUCT_FIELDS = `
  id
  title
  description
  descriptionHtml
  handle
  productType
  tags
  vendor
  priceRange { minVariantPrice { amount currencyCode } }
  featuredImage { url altText }
  images(first: 8) { edges { node { url altText } } }
  variants(first: 20) {
    edges {
      node {
        id
        title
        price { amount currencyCode }
        availableForSale
        quantityAvailable
        currentlyNotInStock
        sellingPlanAllocations(first: 5) {
          edges { node { sellingPlan { id name } } }
        }
        selectedOptions { name value }
      }
    }
  }

  options { name values }
  keyNotes: metafield(namespace: "custom", key: "key_note_cards") {
    references(first: 20) {
      nodes {
        ... on Metaobject {
          id
          handle
          type
          fields {
            key
            value
            reference {
              ... on MediaImage { image { url altText } }
            }
          }
        }
      }
    }
  }
  subtitle:     metafield(namespace: "custom", key: "subtitle")      { value }
  storyTagline: metafield(namespace: "custom", key: "story_tagline") { value }
  storyClose:   metafield(namespace: "custom", key: "story_close")   { value }
  buyNote:      metafield(namespace: "custom", key: "buy_note")      { value }
  accents:      metafield(namespace: "custom", key: "accents")       { value }
  skuDisplay:   metafield(namespace: "custom", key: "sku_display")   { value }
  installment:  metafield(namespace: "custom", key: "installment")   { value }
`;


export const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query, sortKey: TITLE) {
      edges { node { ${PRODUCT_FIELDS} } }
    }
  }
`;

export const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    product(handle: $handle) { ${PRODUCT_FIELDS} }
  }
`;

export const METAOBJECT_BY_HANDLE_QUERY = `
  query GetCharacterCard($handle: MetaobjectHandleInput!) {
    metaobject(handle: $handle) {
      id
      handle
      type
      fields {
        key
        value
        reference {
          ... on MediaImage { image { url altText } }
        }
      }
    }
  }
`;

export const METAOBJECTS_BY_TYPE_QUERY = `
  query GetCharacterCards($first: Int!) {
    metaobjects(type: "character_card", first: $first) {
      nodes {
        id
        handle
        type
        fields {
          key
          value
          reference {
            ... on MediaImage { image { url altText } }
          }
        }
      }
    }
  }
`;

export const BLOG_QUERY = `
  query GetBlog($handle: String!, $first: Int!) {
    blog(handle: $handle) {
      id
      title
      handle
      articles(first: $first, sortKey: PUBLISHED_AT, reverse: true) {
        edges {
          node {
            id
            title
            handle
            excerpt
            publishedAt
            image { url altText }
            authorV2 { name }
          }
        }
      }
    }
  }
`;

export const ARTICLE_QUERY = `
  query GetArticle($blogHandle: String!, $articleHandle: String!) {
    blog(handle: $blogHandle) {
      title
      handle
      articleByHandle(handle: $articleHandle) {
        id
        title
        handle
        contentHtml
        excerpt
        publishedAt
        image { url altText }
        authorV2 { name }
        tags
      }
    }
  }
`;

export async function fetchProducts(opts: { first?: number; query?: string } = {}) {
  const data = await storefrontApiRequest<{ products: { edges: ShopifyProduct[] } }>(
    PRODUCTS_QUERY,
    { first: opts.first ?? 30, query: opts.query ?? null },
  );
  return data?.data?.products?.edges ?? [];
}

export async function fetchProductByHandle(handle: string) {
  const data = await storefrontApiRequest<{ product: ShopifyProductNode | null }>(
    PRODUCT_BY_HANDLE_QUERY,
    { handle },
  );
  return data?.data?.product ?? null;
}

// Canonical URL slug → ordered list of Shopify handles to try.
// The canonical slug itself is always tried first, so renaming the Shopify
// handle to match the slug "just works" with no code change required.
// Additional entries are legacy/aliased handles tried as fallbacks.
// Anything not in this map resolves to the slug itself, so newly published
// Shopify products automatically work at /products/{handle}.
export const SHOPIFY_HANDLE_ALIASES: Record<string, string[]> = {
  hidden: ["hidden", "hidden-eau-de-parfum-30ml"],
  "velvet-sugar": ["velvet-sugar"],
  "the-still": ["the-still", "still"],
  "the-archivist": ["the-archivist"],
  // Shopify product is currently titled "Chapter Vials" but still lives at
  // the legacy handle `discovery-sets`. When the Shopify handle is renamed
  // to `chapter-vials`, the first alias will resolve and the fallback drops out.
  "chapter-vials": ["chapter-vials", "discovery-sets"],
};

/** Ordered list of Shopify handles to try for a given canonical URL slug. */
export function resolveShopifyHandleAliases(slug: string): string[] {
  return SHOPIFY_HANDLE_ALIASES[slug] ?? [slug];
}

/** Back-compat: first (preferred) Shopify handle for a canonical URL slug. */
export function resolveShopifyHandle(slug: string): string {
  return resolveShopifyHandleAliases(slug)[0];
}

/** Back-compat map (preferred handle only) for legacy imports. */
export const SHOPIFY_HANDLE_BY_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(SHOPIFY_HANDLE_ALIASES).map(([slug, aliases]) => [slug, aliases[0]]),
);

/**
 * Fetch a product by canonical URL slug, trying each alias in order.
 * The returned node's `handle` is normalized to the canonical slug so every
 * link in the app stays aligned with `/products/{slug}` regardless of which
 * Shopify handle actually resolved.
 */
export async function fetchProductBySlug(
  slug: string,
): Promise<ShopifyProductNode | null> {
  const aliases = resolveShopifyHandleAliases(slug);
  // Single batched GraphQL request: aliased product(handle:) for each alias.
  const byHandle = await fetchProductsByHandles(aliases);
  for (const handle of aliases) {
    const node = byHandle[handle];
    if (node) return { ...node, handle: slug };
  }
  return null;
}

export const DISCOVERY_SET_HANDLE = "discovery-sets";

/**
 * Handles hidden from the storefront until release.
 * Includes Sap and Stone (pending re-release), the six themed Discovery Set
 * collections, and the two WIP "(Copy)" products that are still being retitled.
 */
export const HIDDEN_HANDLES: ReadonlySet<string> = new Set([
  "sap-and-stone",
  "sap-and-stone-copy",
  "discovery-sets-copy",
  "the-fire-within-collection",
  "the-forest-field-collection",
  "the-orchard-collection",
  "the-sacred-veil-collection",
  "the-selected-works-collection",
  "the-smoke-collection",
]);

/** Match by exact handle, or by known hidden title prefixes (handle drift safe). */
export function isHiddenHandle(handle: string, title?: string): boolean {
  if (HIDDEN_HANDLES.has(handle)) return true;
  if (handle.startsWith("sap-and-stone")) return true;
  if (handle.includes("collection") && /^(the-fire-within|the-forest|the-orchard|the-sacred-veil|the-selected-works|the-smoke)/.test(handle)) {
    return true;
  }
  if (title) {
    const t = title.toLowerCase();
    if (t.startsWith("sap and stone")) return true;
    if (/^the (fire within|forest|orchard|sacred veil|selected works|smoke) collection$/i.test(title.trim())) return true;
  }
  return false;
}

/** Fetch all perfumes (Shopify product_type:Perfume) for storefront listing. */
export async function fetchAllPerfumes(): Promise<ShopifyProductNode[]> {
  const [data, chapterVials] = await Promise.all([
    storefrontApiRequest<{ products: { edges: ShopifyProduct[] } }>(
      PRODUCTS_QUERY,
      { first: 100, query: "(product_type:Perfume) OR (tag:perfume)" },
    ),
    // Resolves to whichever Shopify handle currently exists for Chapter Vials
    // (canonical `chapter-vials` first, then legacy `discovery-sets`) and
    // normalizes the returned handle to the canonical slug.
    fetchProductBySlug("chapter-vials"),
  ]);
  // If the Storefront request failed transiently (network/402/HTTP error),
  // throw so React Query treats it as a retryable error rather than caching
  // an empty success result that renders "No fragrances published yet."
  if (!data || !data.data?.products) {
    throw new Error("Storefront API unavailable");
  }
  const seen = new Set<string>();
  return [
    ...(data.data.products.edges ?? []).map((e) => e.node),
    chapterVials ?? undefined,
  ]
    .filter((p): p is ShopifyProductNode => Boolean(p))
    .filter((p) => !isHiddenHandle(p.handle, p.title))
    .filter((p) => {
      if (seen.has(p.handle)) return false;
      seen.add(p.handle);
      return true;
    });
}

// Batch-fetch multiple products by handle in a single GraphQL request,
// using aliased `product(handle:)` fields. Returns a map keyed by handle.
export async function fetchProductsByHandles(
  handles: string[],
): Promise<Record<string, ShopifyProductNode | null>> {
  if (handles.length === 0) return {};
  const aliasedFields = handles
    .map(
      (h, i) =>
        `p${i}: product(handle: ${JSON.stringify(h)}) { ${PRODUCT_FIELDS} }`,
    )
    .join("\n");
  const query = `query GetProductsByHandle { ${aliasedFields} }`;
  const data = await storefrontApiRequest<Record<string, ShopifyProductNode | null>>(query, {});
  const out: Record<string, ShopifyProductNode | null> = {};
  handles.forEach((h, i) => {
    out[h] = (data?.data as any)?.[`p${i}`] ?? null;
  });
  return out;
}

export async function fetchCharacterCard(handle: string) {
  const data = await storefrontApiRequest<{ metaobject: CharacterCardMetaobject | null }>(
    METAOBJECT_BY_HANDLE_QUERY,
    { handle: { type: "character_card", handle } },
  );
  return data?.data?.metaobject ?? null;
}

export async function fetchCharacterCards(first = 50) {
  const data = await storefrontApiRequest<{ metaobjects: { nodes: CharacterCardMetaobject[] } }>(
    METAOBJECTS_BY_TYPE_QUERY,
    { first },
  );
  return data?.data?.metaobjects?.nodes ?? [];
}

export async function fetchBlog(handle: string, first = 50) {
  const data = await storefrontApiRequest<{ blog: any }>(BLOG_QUERY, { handle, first });
  return data?.data?.blog ?? null;
}

export async function fetchArticle(blogHandle: string, articleHandle: string) {
  const data = await storefrontApiRequest<{ blog: any }>(ARTICLE_QUERY, {
    blogHandle,
    articleHandle,
  });
  return data?.data?.blog?.articleByHandle ?? null;
}

export const FAQ_PAGE_HANDLE = "faq";

export interface ShopifyPage {
  id: string;
  handle: string;
  title: string;
  bodyHtml: string;
}

const PAGE_QUERY = `
  query GetPage($handle: String!) {
    page(handle: $handle) {
      id
      handle
      title
      bodySummary
      body
    }
  }
`;

export async function fetchPageByHandle(handle: string): Promise<ShopifyPage | null> {
  const data = await storefrontApiRequest<{
    page: { id: string; handle: string; title: string; body: string } | null;
  }>(PAGE_QUERY, { handle });
  const p = data?.data?.page;
  if (!p) return null;
  return { id: p.id, handle: p.handle, title: p.title, bodyHtml: p.body };
}

// ---------- helpers for character cards ----------
export function characterField(card: CharacterCardMetaobject, key: string): string | null {
  return card.fields.find((f) => f.key === key)?.value ?? null;
}
export function characterImage(card: CharacterCardMetaobject, key = "image"): ShopifyImage | null {
  return card.fields.find((f) => f.key === key)?.reference?.image ?? null;
}

// ---------- cart ----------
const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
      checkoutUrl
      totalQuantity
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                price { amount currencyCode }
                availableForSale
                quantityAvailable
                currentlyNotInStock
                sellingPlanAllocations(first: 5) {
                  edges { node { sellingPlan { id name } } }
                }
                selectedOptions { name value }

                image { url altText }
                product {
                  handle
                  title
                  featuredImage { url altText }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const CART_CREATE = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_ADD = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_UPDATE = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { id } userErrors { field message } }
  }
`;

const CART_LINES_REMOVE = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { id } userErrors { field message } }
  }
`;

function formatCheckoutUrl(checkoutUrl: string) {
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set("channel", "online_store");
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

function isCartNotFound(errors: Array<{ message: string }>) {
  return errors.some((e) => /cart.*not.*found|does not exist/i.test(e.message));
}

export interface CartLineExtras {
  /** Selling plan to attach (required for Stoq pre-order variants). */
  sellingPlanId?: string | null;
  /** Line-item properties — e.g. Chapter Vials fragrance picks. */
  attributes?: Array<{ key: string; value: string }>;
}

function buildLineInput(input: {
  variantId: string;
  quantity: number;
  sellingPlanId?: string | null;
  attributes?: Array<{ key: string; value: string }>;
}) {
  const line: Record<string, unknown> = {
    quantity: input.quantity,
    merchandiseId: input.variantId,
  };
  if (input.sellingPlanId) line.sellingPlanId = input.sellingPlanId;
  if (input.attributes && input.attributes.length > 0) line.attributes = input.attributes;
  return line;
}

export async function createShopifyCart(
  input: { variantId: string; quantity: number } & CartLineExtras,
) {
  const data = await storefrontApiRequest<any>(CART_CREATE, {
    input: { lines: [buildLineInput(input)] },
  });
  const result = data?.data?.cartCreate;
  if (!result || result.userErrors?.length) {
    if (result?.userErrors?.length) console.error("cartCreate userErrors", result.userErrors);
    return null;
  }
  const cart = result.cart;
  const lineId = cart.lines.edges[0]?.node?.id;
  if (!lineId || !cart.checkoutUrl) return null;
  return { cartId: cart.id, checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), lineId };
}

export async function addLineToShopifyCart(
  cartId: string,
  input: { variantId: string; quantity: number } & CartLineExtras,
) {
  const data = await storefrontApiRequest<any>(CART_LINES_ADD, {
    cartId,
    lines: [buildLineInput(input)],
  });
  const errors = data?.data?.cartLinesAdd?.userErrors ?? [];
  if (isCartNotFound(errors)) return { success: false, cartNotFound: true } as const;
  if (errors.length) {
    console.error("cartLinesAdd userErrors", errors);
    return { success: false } as const;
  }

  const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges ?? [];
  const line = lines.find((l: any) => l.node.merchandise.id === input.variantId);
  return { success: true, lineId: line?.node?.id as string | undefined } as const;
}

export async function updateShopifyCartLine(cartId: string, lineId: string, quantity: number) {
  const data = await storefrontApiRequest<any>(CART_LINES_UPDATE, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });
  const errors = data?.data?.cartLinesUpdate?.userErrors ?? [];
  if (isCartNotFound(errors)) return { success: false, cartNotFound: true } as const;
  if (errors.length) return { success: false } as const;
  return { success: true } as const;
}

export async function removeLineFromShopifyCart(cartId: string, lineId: string) {
  const data = await storefrontApiRequest<any>(CART_LINES_REMOVE, { cartId, lineIds: [lineId] });
  const errors = data?.data?.cartLinesRemove?.userErrors ?? [];
  if (isCartNotFound(errors)) return { success: false, cartNotFound: true } as const;
  if (errors.length) return { success: false } as const;
  return { success: true } as const;
}

export async function fetchCartStatus(
  cartId: string,
): Promise<
  | { status: "ok"; cart: ShopifyCartSnapshot }
  | { status: "missing" }
  | { status: "error" }
> {
  try {
    const data = await storefrontApiRequest<any>(CART_QUERY, { id: cartId });
    // No data envelope at all → treat as transient (network, 402, etc).
    if (!data || !("data" in data)) return { status: "error" };
    const cart = data?.data?.cart;
    // GraphQL explicitly returned cart: null → cart truly doesn't exist on Shopify.
    if (cart === null) return { status: "missing" };
    if (!cart) return { status: "error" };
    return {
      status: "ok",
      cart: { ...cart, checkoutUrl: formatCheckoutUrl(cart.checkoutUrl) },
    };
  } catch {
    return { status: "error" };
  }
}


export function formatMoney(amount: string | number, currency: string) {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}
